import { Trans, useTranslation } from '@ecomm/i18n'
import { Modal } from '@ecomm/shared-components/lib/Modal'
import { InformationIcon } from '@ecomm/shared-icons'
//import { toHtmlId } from '@simplisafe/ewok'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { Abbr247 } from '../A11yHelpers/Abbr247'

// TODO [@simplisafe/ewok@>=4.9.1]: Remove this function when we upgrade ewok to a version that includes toHtmlId
const toHtmlId = (title: string): string => title.replace(/\W/g, '_')

type InformationModalProps = {
  readonly title: string
  readonly modalDescription: string | ReactNode
}

function InformationModal({ title, modalDescription }: InformationModalProps) {
  const [isOpen, setIsOpen] = useState(false)
  const handleOpenToggle = () => {
    setIsOpen(!isOpen)
  }
  const id = toHtmlId(title)
  const { t } = useTranslation('common')

  return (
    <>
      <button
        className="border-none bg-transparent cursor-pointer p-0"
        data-testid="InformationModalButton"
        onClick={handleOpenToggle}
      >
        <span className="sr-only">
          {t('microcopy.more-info-about')} {title}
        </span>
        <InformationIcon className="h-5 md:h-6 w-5 md:w-6" />
      </button>
      <Modal
        isOpen={isOpen}
        onRequestClose={() => {
          setIsOpen(false)
        }}
        aria={{ labelledby: id }}
        size="small"
      >
        <div className="p-5 grid gap-5 max-w-xs md:max-w-md">
          <h2 id={id} className="m-0">
            {title}
          </h2>
          <p className="m-0 text-sm">{modalDescription}</p>
          <button
            className="btn btn-solid-primary"
            data-testid="InformationModalCloseButton"
            onClick={() => {
              setIsOpen(false)
            }}
          >
            {t('microcopy.close')}
          </button>
        </div>
      </Modal>
    </>
  )
}

type ComparisonRowProps = {
  readonly title: string
  readonly titleSeparator?: string
  readonly description: string
  readonly children: ReactNode
  readonly modalDescription?: string | ReactNode
}

export function ComparisonRow({
  title,
  titleSeparator = ':',
  description,
  children,
  modalDescription
}: ComparisonRowProps) {
  return (
    <tr
      className="border-b border-0 border-solid border-neutral-medium last:border-b-0 grid grid-cols-subgrid grid-rows-subgrid col-span-full row-span-3"
      data-testid="ComparisonRow"
    >
      <th
        scope="row"
        abbr={title}
        className="font-normal text-left row-span-3 flex flex-col md:flex-row items-start md:items-center py-2 md:py-6 gap-1 md:gap-2"
      >
        <p className="text-sm md:text-lg m-0">
          <strong>
            {title}
            {titleSeparator}
          </strong>{' '}
          <Trans components={{ abbr247: <Abbr247>text</Abbr247> }}>
            {description}
          </Trans>
        </p>
        {modalDescription && (
          <InformationModal title={title} modalDescription={modalDescription} />
        )}
      </th>
      {children}
    </tr>
  )
}
